import React, { useEffect, useState } from 'react'
import SuccessAlert from '../../components/Alerts/SuccessAlert';
import FailAlert from '../../components/Alerts/FailAlert';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AiFillDelete } from 'react-icons/ai';
import { useMutation } from '@apollo/client';
import { ALL_SUBSCRIPTION, UpdateSubscription, useSubscription } from '../../graphql';
import { useParams } from 'react-router-dom';
import LoadingWidget from '../../components/LoadingWidget/LoadingWidget';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { t } from 'i18next';

const formSchema = z.object({
  planName: z.string().min(1, { message: t('nameRequire') }),
  price: z.number().optional(),
  discount: z.number().optional(),
  features: z.array(z.object({ name: z.string().min(1, t('Feature name cannot be empty')) })),
})

type FormType = z.infer<typeof formSchema>

const EditSubscription = () => {
  const { id } = useParams();

  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState<any>("");

  const { register, getValues, handleSubmit, formState: { errors }, setValue, watch } = useForm<FormType>({
    resolver: zodResolver(formSchema)
  })

  const features = watch('features', [{ name: "" }]);

  const addFeature = () => {
    const currentFeatures = getValues('features');
    setValue('features', [...currentFeatures, { name: '' }]);
  };

  const deleteFeature = (indexToDelete: number) => {
    const updatedFeatures = features.filter((_, index) => index !== indexToDelete);
    setValue('features', updatedFeatures);
  };

  const [initialLoading, setInitialLoading] = useState(true);

  const [Subscription, { loading, data: dataAllSubscriptions }] = useSubscription();

  useEffect(() => {
    (async () => {
      await Subscription({
        variables: { subscriptionId: id }
      })

      setInitialLoading(false)
    })()
  }, []);

  useEffect(() => {
    if (dataAllSubscriptions) {
      const newFeatures: any[] = [];
      setValue("planName", dataAllSubscriptions.subscription.planName);
      setValue("price", dataAllSubscriptions.subscription.price ? dataAllSubscriptions.subscription.price : 0);
      setValue("discount", dataAllSubscriptions.subscription.discount);
      dataAllSubscriptions.subscription.features.map(feature => (
        newFeatures.push({ name: feature })
      ))
      setValue("features", newFeatures);
      setPublish(dataAllSubscriptions.subscription.published);
    }
  }, [dataAllSubscriptions])

  // Update
  const [updateSubscription, { error, loading: updateLoading }] = useMutation(UpdateSubscription, {
    refetchQueries: [{
      query: ALL_SUBSCRIPTION
    }]
  });

  const Submit = (data: FormType) => {

    const features: string[] = [];

    data.features.map(feature => {
      features.push(feature.name)
    })

    updateSubscription({
      variables: {
        updateSubscriptionId: id,
        content: {
          discount: data.discount,
          features: features,
          planName: data.planName,
          price: data.price,
          published: publish,
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('updateSuccess'));
      setSuccess(true);
    }
  }

  useEffect(() => {
    const fieldCount = Object.keys(formSchema.shape).length;

    // if all the errors show up
    if (Object.keys(errors).length === fieldCount) {
      setFail(true);
      setFailMsg(t('formNonComplete'));
      return
    } else {

      if (errors.planName) {
        setFail(true);
        setFailMsg(t('nameRequire'));
        return
      }

      if (errors.price) {
        setFail(true);
        setFailMsg(errors.price?.message);
        return
      }

      if (errors.features) {
        setFail(true);
        setFailMsg(errors.features[0]?.name?.message);
        return
      }
    }
  }, [errors])

  const [publish, setPublish] = useState(true)

  return (
    <section className='my-4 mx-7'>
      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      <div className='flex flex-col-reverse md:flex-row md:justify-between md:items-center'>
        <div>
          <h3 className='text-2xl font-semibold'>{t('edit subscription management')}</h3>
        </div>
        <div>
          <Breadcrumbs />
        </div>
      </div>

      {initialLoading || loading ?
        <LoadingWidget />
        :
        <form onSubmit={handleSubmit(Submit)} className='relative p-2 mt-3 bg-white rounded-md'>
          {updateLoading &&
            <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
              <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
              </svg>
            </div>
          }

          <div className="flex items-center pb-3">
            <input type="checkbox" checked={publish} onChange={() => setPublish(!publish)} className="relative w-4 h-4 border border-gray-300 rounded appearance-none peer bg-gray-50 checked:bg-primary checked:border-0" />
            <label className="mx-2 font-medium text-gray-800">{t('Publish')}</label>
            <svg className="absolute w-4 h-4 p-0.5 text-white hidden peer-checked:block pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>

          <div className="mb-4">
            <label htmlFor="planName" className="block mb-2 text-sm font-medium text-gray-900">{t('name')}</label>
            <input type="text" id="planName" {...register("planName", { required: true })} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('name')} />
          </div>
          <div className="mb-4">
            <label htmlFor="price" className="block mb-2 text-sm font-medium text-gray-900">{t("price")}</label>
            <input type="number" min={0} id="price" defaultValue={getValues('price')} onChange={(e) => setValue('price', e.target.valueAsNumber)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" />
          </div>
          <div className="mb-4">
            <label htmlFor="discount" className="block mb-2 text-sm font-medium text-gray-900">{t("discount")}</label>
            <input type="number" min={0} id="discount" defaultValue={getValues('discount')} onChange={(e) => setValue('discount', e.target.valueAsNumber)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" />
          </div>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-900">{t('Features')}</label>

            {features.map((feature: any, index: number) => (
              <div className='flex items-center gap-2' key={index}>
                <input
                  {...register(`features.${index}.name` as const)}
                  className="bg-gray-50 mb-2 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5"
                  placeholder={t('name')}
                />

                {index !== 0 &&
                  <button type="button" onClick={() => deleteFeature(index)} className='p-2 mb-2 text-red-600 bg-white border rounded'>
                    <AiFillDelete size={20} />
                  </button>
                }
              </div>
            ))}

            <div className='pt-5'>
              <button type='button' onClick={addFeature} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                  <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                </svg>

                <span className="mx-2">
                  {t('save')}
                </span>
              </button>
            </div>
          </div>

          <div className='flex justify-end pt-6'>
            <button className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
              </svg>

              <span className="mx-2">
                {t('save')}
              </span>
            </button>
          </div>
        </form>
      }
    </section>
  )
}

export default EditSubscription